import React from "react";
import { useLocation } from "react-router-dom";
import Navbar from "./navbar";
import Footer from "./footer";
import PageLoadState, { Skel } from "./PageLoadState";

// What a route shows while its page's code downloads (each page is its own chunk, src/App.js). Only on a first
// visit: a link to another page keeps the current page on screen until the next one is ready.
// The navbar and footer stay where they always are and the window's height is kept between them; the grey
// bars appear only after 200 ms, so a fast load never flashes them (no "Loading..." text, no spinner).
// Styles: .fn-routewait in src/styles/foundation.css.
const RouteFallback = () => (
  <>
    <Navbar />
    <main className="fn-wrap fn-routewait" aria-busy="true">
      <div className="fn-routewait-in" aria-hidden="true">
        <Skel w={120} h={14} />
        <Skel w="min(560px, 90%)" h={28} />
        <Skel w="min(680px, 100%)" h={16} />
        <div className="fn-pageskel-cards">
          {[0, 1, 2, 3].map((i) => (
            <Skel key={i} h={120} r={16} />
          ))}
        </div>
      </div>
    </main>
    <Footer />
  </>
);

// A page's code that could not be downloaded (offline, or a new release replaced the file): the page says so
// with a Try again button that reloads it, never a blank window. Any other error is thrown on as before.
const isChunkError = (e) => !!e && (e.name === "ChunkLoadError" || /Loading (CSS )?chunk [\w-]+ failed/i.test(String(e.message)));

class RouteFailedBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, pathname: props.pathname };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  static getDerivedStateFromProps(props, state) {
    // another address: try again
    if (props.pathname !== state.pathname) return { error: null, pathname: props.pathname };
    return null;
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (!isChunkError(error)) throw error;
    return <PageLoadState failed text="Its code couldn't be downloaded. Check your connection and try again." />;
  }
}

export const RouteFailed = ({ children }) => {
  const { pathname } = useLocation();
  return <RouteFailedBoundary pathname={pathname}>{children}</RouteFailedBoundary>;
};

export default RouteFallback;
